import { observer } from 'mobx-react-lite';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { round } from 'lodash';

import { useRootStore } from 'stores/RootStore';

import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { EXPENSE_AMOUNT_LABEL, EXPENSE_INPUT_METHOD_RADIO_BUTTON_ITEMS, EXPENSE_RATIO_LABEL } from '../constants';
import NumericInput from 'components/shared/inputs/NumericInput';
import { INCOME_APPROACH_BASIS_TYPES, incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { calculateOperatingExpenses } from '../operating-expenses/expense-history/helpers';
import { formatCurrencyFloat, formatPercentageString } from 'shared/utils/numberFormatters';
import { EXPENSE_RATIO_INPUT_METHOD } from 'shared/constants/expenses';

const SummarizedForecastPage = observer(({ form }: any) => {
  const { underwritingPageStore } = useRootStore();
  const { underwritingData = {} as UnderwritingOutput } = underwritingPageStore;

  const effectiveGrossIncome = useMemo(
    () =>
      incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData, INCOME_APPROACH_BASIS_TYPES.projected),
    [underwritingData]
  );

  const expenseForecastInputMethod = form.$('incomeApproach.expenses.expenseForecastInputMethod');
  const expenseForecastAmountField = form.$('incomeApproach.expenses.totalExpenseForecastAmount');
  const expenseForecastRatioField = form.$('incomeApproach.expenses.expenseForecastRatio');

  const inputMethod = expenseForecastInputMethod.value;
  const isAmountMethod = inputMethod !== EXPENSE_RATIO_INPUT_METHOD;

  useEffect(() => {
    const baseValue = isAmountMethod ? expenseForecastAmountField.value : expenseForecastRatioField.value;
    const { amount, ratio } = calculateOperatingExpenses(effectiveGrossIncome, baseValue || 0, inputMethod);

    expenseForecastRatioField.set(ratio);
    expenseForecastAmountField.set(amount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (value: number) => {
    const sanitizedValue = !isNaN(value) ? Math.abs(round(value, isAmountMethod ? 2 : 4)) : 0;

    if (
      (isAmountMethod && sanitizedValue === expenseForecastAmountField.value) ||
      (!isAmountMethod && sanitizedValue === expenseForecastRatioField.value)
    ) {
      return;
    }

    try {
      const { amount, ratio } = calculateOperatingExpenses(effectiveGrossIncome, sanitizedValue || 0, inputMethod);

      expenseForecastRatioField.set(ratio);
      expenseForecastAmountField.set(amount);
    } catch (error) {
      console.error('Error setting field', error);
    }
  };

  const inputProps = isAmountMethod
    ? {
        label: EXPENSE_AMOUNT_LABEL,
        value: expenseForecastAmountField.value,
        decimalScale: 2,
        InputProps: {
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        },
      }
    : {
        label: EXPENSE_RATIO_LABEL,
        value: expenseForecastRatioField.value,
        // TODO: fix max logic in NumericInput
        // max: 100,
        decimalScale: 4,
        InputProps: {
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        },
      };

  return (
    <Grid container sx={{ pt: 2 }}>
      <Stack spacing={2} sx={{ width: '100%' }}>
        <FormControl>
          <FormLabel>Expense Forecast Input Method</FormLabel>
          <RadioGroup {...expenseForecastInputMethod.bind()} row sx={{ minHeight: 40, mt: 0, mb: 0 }}>
            {EXPENSE_INPUT_METHOD_RADIO_BUTTON_ITEMS.map(({ value, label }) => (
              <FormControlLabel control={<Radio />} key={value} sx={{ mb: 3 / 4 }} {...{ label, value }} />
            ))}
          </RadioGroup>
        </FormControl>

        <Grid item xs={3}>
          <NumericInput
            size="small"
            // @ts-ignore
            onChange={handleInputChange}
            margin="normal"
            fullWidth
            placeholder="0.00"
            allowNegative={false}
            thousandSeparator
            {...inputProps}
          />

          <Typography sx={{ mt: 3 }}>
            <strong>Total Operating Expenses: </strong>{' '}
            {isAmountMethod
              ? formatPercentageString(expenseForecastRatioField.value)
              : formatCurrencyFloat(expenseForecastAmountField.value)}
          </Typography>
        </Grid>
      </Stack>
    </Grid>
  );
});

export { SummarizedForecastPage };
